import React from 'react';
import { motion } from 'framer-motion';
import { Button as ShadcnButton } from '../ui/button';
import { cn } from '../../lib/utils';

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  href?: string;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  href,
  onClick,
  disabled,
  ...props
}: ButtonProps) {
  // Extract our custom props from the rest
  const { type, form, formAction, formEncType, formMethod, formNoValidate, formTarget, name, value } = props;
  const buttonClasses = cn('', className);

  // Map our variants to Shadcn variants
  const shadcnVariant = variant === 'primary' ? 'default' :
                       variant === 'secondary' ? 'secondary' :
                       variant === 'outline' ? 'outline' : 'default';

  const shadcnSize = size === 'sm' ? 'sm' :
                    size === 'lg' ? 'lg' : 'default';

  if (href) {
    return (
      <motion.a
        href={href}
        className={buttonClasses}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <ShadcnButton
          variant={shadcnVariant}
          size={shadcnSize}
          disabled={disabled}
          onClick={onClick}
          className="bg-zwheel-black text-zwheel-white hover:bg-zwheel-gray-800"
        >
          {children}
        </ShadcnButton>
      </motion.a>
    );
  }

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={buttonClasses}
    >
      <ShadcnButton
        variant={shadcnVariant}
        size={shadcnSize}
        disabled={disabled}
        onClick={onClick}
        type={type}
        form={form}
        formAction={formAction}
        formEncType={formEncType}
        formMethod={formMethod}
        formNoValidate={formNoValidate}
        formTarget={formTarget}
        name={name}
        value={value}
        className={cn(
          shadcnVariant === 'default' && "bg-zwheel-black text-zwheel-white hover:bg-zwheel-gray-800",
          shadcnVariant === 'secondary' && "bg-zwheel-gray-100 text-zwheel-gray-900 hover:bg-zwheel-gray-200",
          shadcnVariant === 'outline' && "border-zwheel-gray-300 text-zwheel-gray-700 hover:bg-zwheel-gray-50"
        )}
      >
        {children}
      </ShadcnButton>
    </motion.div>
  );
}
